import { Box, ButtonBase, Grid, Stack, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { CollapseChevron } from "components/CollapseChevron";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { TextCellWithTooltip } from "components/DataGridCell";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { StatusChip } from "components/StatusChip";
import { safeParseFloat } from "encamp-shared/src/utils/safeParseNumber";
import { WasteScheduleAdherenceIssueCode } from "generated-graphql/graphql";
import { ReactNode, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FieldValue } from "routes/Customer/Waste/Containers/containerSections";
import {
  formatInvoiceDate,
  formatInvoiceMoney,
  LINE_KIND_LABELS,
  NO_VALUE,
} from "routes/Customer/Waste/Invoices/utils";
import { FieldWarningIndicator } from "routes/Customer/Waste/Manifests/FieldWarningIndicator";

import { ExceptionRow, STATUS_COLOUR } from "./columns";
import {
  ComparisonCell,
  ComparisonRow,
  comparisonRows,
  invoiceEvidence,
  timingHeadline,
} from "./comparison";
import { InvoiceLinks } from "./InvoiceLinks";
import {
  facilityLabel,
  formatWindow,
  KIND_LABELS,
  serviceLabel,
  STATUS_LABELS,
  withVendorTerm,
} from "./labels";

type InvoiceLine = ExceptionRow["billedLines"][number];

/** A titled part of the drawer, set apart by space rather than a border or a panel. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box component="section">
      <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

/** The timing where it was graded, the status beside it, and what it means. */
function Verdict({ row }: { row: ExceptionRow }) {
  const headline = timingHeadline(row);
  const window = row.occurrence
    ? formatWindow(row.occurrence.scheduledWindowStart, row.occurrence.scheduledWindowEnd)
    : null;
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          {headline == null ? null : <Typography variant="h5">{headline.text}</Typography>}
          <Typography variant="body2" color="text.secondary">
            {[row.vendor.name, window].filter(Boolean).join(" · ")}
          </Typography>
        </Stack>
        <Box sx={{ flexShrink: 0 }}>
          <StatusChip color={STATUS_COLOUR[row.status]} label={STATUS_LABELS[row.status]} />
        </Box>
      </Stack>
      <Typography variant="body2" sx={{ mt: 2 }}>
        {row.explanation}
      </Typography>
    </Box>
  );
}

/**
 * What to obtain from the vendor. A finding the explanation already stated is
 * not stated again; only what to ask for is added.
 */
function WhatToAskFor({ row }: { row: ExceptionRow }) {
  const ambiguous = row.issues.some(
    (entry) => entry.code === WasteScheduleAdherenceIssueCode.AmbiguousScheduleMatch
  );
  // The windows the audit could not choose between. Several candidates are
  // otherwise one window the vendor published twice, which is not a choice.
  const windows = useMemo(() => {
    const texts = row.candidates.map((candidate) => {
      const window = formatWindow(candidate.scheduledWindowStart, candidate.scheduledWindowEnd);
      return candidate.vendorServiceOrderNumber
        ? `${window} · ${candidate.vendorServiceOrderNumber}`
        : window;
    });
    return [...new Set(texts)];
  }, [row.candidates]);
  if (row.issues.length === 0) return null;
  return (
    <Section title="What to ask for">
      <Stack spacing={1.5}>
        {row.issues.map((issue) => (
          <Box key={issue.code}>
            {row.explanation.includes(issue.message) ? null : (
              <Typography variant="body2" fontWeight={600}>
                {issue.message}
              </Typography>
            )}
            <Typography variant="body2">{issue.clarification}</Typography>
          </Box>
        ))}
        {ambiguous && windows.length > 1 ? (
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {windows.map((text) => (
              <Typography key={text} component="li" variant="body2">
                {text}
              </Typography>
            ))}
          </Box>
        ) : null}
      </Stack>
    </Section>
  );
}

/**
 * Rows grow to fit a caption, so every cell starts at the top with the same
 * padding and the first lines across a row stay level.
 */
const CELL_PADDING = { py: 1.25 };

function Value({ value }: { value: ComparisonCell }) {
  if (value == null) return <Box sx={CELL_PADDING}>{NO_VALUE}</Box>;
  return (
    <Box sx={CELL_PADDING}>
      <Typography variant="body2">{value.text}</Typography>
      {value.caption == null ? null : (
        <Typography variant="caption" color="text.secondary" display="block">
          {value.caption}
        </Typography>
      )}
    </Box>
  );
}

const COMPARISON_COLUMNS: CustomGridColDef<ComparisonRow>[] = [
  {
    field: "label",
    // The rows name themselves; the header is left blank on screen and kept
    // for a screen reader, which announces each cell under it.
    headerName: "Field",
    renderHeader: () => (
      <Box component="span" sx={visuallyHidden}>
        Field
      </Box>
    ),
    // Flexed rather than fixed so all three columns fit a phone-width drawer.
    flex: 0.7,
    minWidth: 96,
    renderCell: ({ row: entry }) => (
      <Stack direction="row" alignItems="center" sx={CELL_PADDING}>
        {/* Labels are short words; they wrap between them, never inside one. */}
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "normal" }}>
          {entry.label}
        </Typography>
        {entry.differs ? (
          <FieldWarningIndicator
            inline
            tooltip="The schedule and the invoice disagree here."
            ariaLabel="Differs"
          />
        ) : null}
      </Stack>
    ),
  },
  {
    field: "schedule",
    headerName: "Schedule",
    flex: 1,
    minWidth: 100,
    renderCell: ({ row: entry }) => <Value value={entry.schedule} />,
  },
  {
    field: "invoice",
    headerName: "Invoice",
    flex: 1,
    minWidth: 100,
    renderCell: ({ row: entry }) => <Value value={entry.invoice} />,
  },
];

/** The schedule set beside the invoices, one fact per row; a disagreement is flagged. */
function Comparison({ row }: { row: ExceptionRow }) {
  const rows = useMemo(() => comparisonRows(row), [row]);
  return (
    <DataGrid<ComparisonRow>
      aria-label="Schedule and invoice comparison"
      rows={rows}
      columns={COMPARISON_COLUMNS}
      getRowId={(entry) => entry.label}
      getRowHeight={() => "auto"}
      autoHeight
      hideFooter
      disableColumnMenu
      disableColumnSorting
      disableColumnResize
      disableRowSelectionOnClick
      sx={{ "& .MuiDataGrid-cell": { alignItems: "flex-start" } }}
    />
  );
}

/** What the importer read a charge as, with the vendor's own term beside it. */
const lineService = (line: InvoiceLine) =>
  withVendorTerm(serviceLabel(line.serviceKind, line.serviceSump), line.sourceServiceType);

const INVOICE_LINE_COLUMNS: CustomGridColDef<InvoiceLine>[] = [
  {
    field: "description",
    headerName: "Description",
    flex: 1,
    minWidth: 140,
    // A vendor's own wording runs long; the cell keeps one line and the full
    // text is a hover away rather than lost.
    renderCell: ({ value }) => <TextCellWithTooltip>{value ?? NO_VALUE}</TextCellWithTooltip>,
  },
  {
    field: "kind",
    headerName: "Billed as",
    width: 96,
    // The same wording the invoice's own line table uses, rather than the enum.
    valueGetter: (_value, line) => LINE_KIND_LABELS[line.kind] ?? line.kind,
  },
  {
    field: "service",
    headerName: "Service",
    width: 150,
    // Shows why a visit was graded as the service it was.
    valueGetter: (_value, line) => lineService(line),
    renderCell: ({ value }) => <TextCellWithTooltip>{value}</TextCellWithTooltip>,
  },
  {
    field: "serviceDate",
    headerName: "Service date",
    width: 108,
    // A visit grouped by service order can span days, so a line carries its own
    // date rather than borrowing the visit's.
    valueGetter: (_value, line) => formatInvoiceDate(line.serviceDate),
  },
  {
    field: "billedAmount",
    headerName: "Amount",
    width: 104,
    align: "right",
    headerAlign: "right",
    // The wire value is an exact decimal string, so sort on a number rather than
    // letting the grid compare it lexically.
    type: "number",
    valueGetter: (_value, line) => safeParseFloat(line.billedAmount),
    valueFormatter: (value: number | null) => formatInvoiceMoney(value),
  },
];

/**
 * Largest first so the first page explains the total. Ties are settled here by
 * description, since one visit can carry the same charge twice at the same
 * price, leaving the grid a single sort key and no sort-order badges.
 */
function byAmountThenDescription(left: InvoiceLine, right: InvoiceLine): number {
  const byAmount =
    (safeParseFloat(right.billedAmount) ?? 0) - (safeParseFloat(left.billedAmount) ?? 0);
  return byAmount !== 0
    ? byAmount
    : (left.description ?? "").localeCompare(right.description ?? "");
}

function InvoiceLines({ lines }: { lines: readonly InvoiceLine[] }) {
  const rows = useMemo(() => [...lines].sort(byAmountThenDescription), [lines]);
  // A column that says the same thing on every line says it once too often; the
  // drawer's title already names the service.
  const oneService = useMemo(() => new Set(lines.map(lineService)).size <= 1, [lines]);
  return (
    <DataGrid<InvoiceLine>
      rows={rows}
      columns={INVOICE_LINE_COLUMNS}
      columnVisibilityModel={{ service: !oneService }}
      getRowId={(line) => line.id}
      autoHeight
      // The premium grid does not page unless asked, and a visit can carry
      // fifty lines.
      pagination
      disableColumnMenu
      disableRowSelectionOnClick
      initialState={{
        pagination: { paginationModel: { pageSize: 10, page: 0 } },
        sorting: { sortModel: [{ field: "billedAmount", sort: "desc" }] },
      }}
      pageSizeOptions={[10, 25, 50]}
      // Inside the invoice section's own outline, a second border would double it.
      sx={{ border: 0 }}
    />
  );
}

const sumBilled = (lines: readonly InvoiceLine[]) =>
  lines.reduce((total, line) => total + (safeParseFloat(line.billedAmount) ?? 0), 0);

/** The same outline the data grids in this drawer draw, so the two read as siblings. */
const OUTLINED = { border: 1, borderColor: "divider", borderRadius: 1 };

/**
 * The invoice the comparison was drawn from. Its reference, manifest and total
 * stay in view; a row at the foot opens the lines, the evidence behind every
 * value in the comparison, directly beneath it.
 */
function Evidence({ row }: { row: ExceptionRow }) {
  const [showLines, setShowLines] = useState(false);
  const linesId = useId();
  const lines = invoiceEvidence(row)?.lines ?? [];
  if (lines.length === 0) return null;
  const count = lines.length === 1 ? "1 invoice line" : `${lines.length} invoice lines`;
  return (
    <Box component="section" sx={OUTLINED}>
      <Typography variant="subtitle2" component="h3" sx={{ px: 2, pt: 1.5, pb: 1 }}>
        Invoice
      </Typography>
      <Grid container spacing={2} sx={{ px: 2, pb: 2 }}>
        <FieldValue label="Invoice">
          <InvoiceLinks lines={lines} />
        </FieldValue>
        <FieldValue label="Manifest">{row.visit?.manifestNumber ?? NO_VALUE}</FieldValue>
        <FieldValue label="Billed">
          {formatInvoiceMoney(row.visit?.billedAmount ?? sumBilled(lines))}
        </FieldValue>
      </Grid>
      <ButtonBase
        aria-expanded={showLines}
        aria-controls={linesId}
        onClick={() => setShowLines((current) => !current)}
        sx={{
          width: "100%",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderTop: 1,
          borderColor: "divider",
          color: "text.secondary",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Typography variant="body2" component="span">
          {count}
        </Typography>
        <CollapseChevron collapsed={!showLines} />
      </ButtonBase>
      {showLines ? (
        <Box id={linesId} sx={{ borderTop: 1, borderColor: "divider" }}>
          <InvoiceLines lines={lines} />
        </Box>
      ) : null}
    </Box>
  );
}

export function ResultDrawer({ row, onClose }: { row: ExceptionRow | null; onClose: () => void }) {
  // Focus moves into the drawer as it opens, which would otherwise scroll the
  // page behind it to the element; the invoice audit drawer does the same.
  const contentRef = useRef<HTMLDivElement>(null);
  const rowId = row?.id;
  useLayoutEffect(() => {
    if (rowId != null) contentRef.current?.focus({ preventScroll: true });
  }, [rowId]);
  return (
    <RightDrawerShell
      open={row != null}
      onClose={onClose}
      title={
        row == null ? null : (
          <Typography variant="h6" component="h2" sx={{ whiteSpace: "normal" }}>
            {[
              facilityLabel(row.facilityName, row.customerFacilityId) ?? "Unidentified facility",
              row.kind ? KIND_LABELS[row.kind] : "Service unclear",
            ].join(" · ")}
          </Typography>
        )
      }
    >
      {row == null ? null : (
        <Box
          ref={contentRef}
          role="region"
          aria-label="Schedule adherence result"
          tabIndex={-1}
          sx={{ outline: "none" }}
        >
          <Stack spacing={3} sx={{ overflowWrap: "anywhere" }}>
            <Verdict row={row} />
            <WhatToAskFor row={row} />
            <Comparison row={row} />
            <Evidence row={row} />
          </Stack>
        </Box>
      )}
    </RightDrawerShell>
  );
}
